import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook for managing user permissions in the frontend.
 * Fetches permissions from /api/rbac/my-permissions and provides
 * utility functions to check if user has specific permissions.
 * 
 * @returns {Object} Permission state and helper functions
 */
export function usePermissions() {
  const [permissions, setPermissions] = useState([]);
  const [accessibleMenus, setAccessibleMenus] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleInfo, setRoleInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch user permissions from the backend
   */
  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/api/rbac/my-permissions`, {
        withCredentials: true
      });
      
      const data = response.data;
      setPermissions(data.permissions || []);
      setAccessibleMenus(data.accessible_menus || {});
      setIsAdmin(data.is_admin || false);
      setRoleInfo(data.role_info || null);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err.message);
      // Default to empty permissions on error (fail-safe)
      setPermissions([]);
      setAccessibleMenus({});
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch permissions on mount
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission string (e.g., "crm.leads.read")
   * @returns {boolean}
   */
  const hasPermission = useCallback((permission) => {
    if (isAdmin) return true;
    return permissions.includes(permission);
  }, [permissions, isAdmin]);

  /**
   * Check if user has ANY of the specified permissions
   * @param {string[]} permissionList - Array of permission strings
   * @returns {boolean}
   */
  const hasAnyPermission = useCallback((permissionList) => {
    if (isAdmin) return true;
    return permissionList.some(p => permissions.includes(p));
  }, [permissions, isAdmin]);

  /**
   * Check if user has ALL of the specified permissions
   * @param {string[]} permissionList - Array of permission strings
   * @returns {boolean}
   */
  const hasAllPermissions = useCallback((permissionList) => {
    if (isAdmin) return true;
    return permissionList.every(p => permissions.includes(p));
  }, [permissions, isAdmin]);

  /**
   * Check if user can access a specific menu
   * @param {string} menuKey - Menu key (e.g., "crm_leads", "franchise")
   * @returns {boolean}
   */
  const canAccessMenu = useCallback((menuKey) => {
    if (isAdmin) return true;
    return accessibleMenus[menuKey] === true;
  }, [accessibleMenus, isAdmin]);

  /**
   * Refresh permissions (useful after role changes)
   */
  const refresh = useCallback(() => {
    return fetchPermissions();
  }, [fetchPermissions]);

  return {
    // State
    permissions,
    accessibleMenus,
    isAdmin,
    roleInfo,
    loading,
    error,
    
    // Permission checkers
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessMenu,
    
    // Actions
    refresh
  };
}

/**
 * Permission constants for easy reference in components
 * These match the backend PERMISSIONS in /app/backend/models/rbac.py
 */
export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: 'dashboard.view',
  
  // Daily Logs
  LOGS_READ: 'logs.daily_log.read',
  LOGS_WRITE: 'logs.daily_log.write',
  LOGS_DELETE: 'logs.daily_log.delete',
  LOGS_HISTORY: 'logs.history.read',
  LOGS_LEADERBOARD: 'logs.leaderboard.read',
  
  // CRM - Companies
  CRM_COMPANIES_READ: 'crm.companies.read',
  CRM_COMPANIES_WRITE: 'crm.companies.write',
  CRM_COMPANIES_DELETE: 'crm.companies.delete',
  
  // CRM - Leads
  CRM_LEADS_READ: 'crm.leads.read',
  CRM_LEADS_WRITE: 'crm.leads.write',
  CRM_LEADS_DELETE: 'crm.leads.delete',
  
  // CRM - Inbox
  CRM_INBOX_READ: 'crm.inbox.read',
  CRM_INBOX_WRITE: 'crm.inbox.write',
  
  // CRM - Tasks
  CRM_TASKS_READ: 'crm.tasks.read',
  CRM_TASKS_WRITE: 'crm.tasks.write',
  CRM_TASKS_DELETE: 'crm.tasks.delete',
  
  // CRM - Communications
  CRM_COMMUNICATIONS_READ: 'crm.communications.read',
  CRM_COMMUNICATIONS_WRITE: 'crm.communications.write',
  CRM_COMMUNICATIONS_DELETE: 'crm.communications.delete',
  
  // Franchise - Leads
  FRANCHISE_LEADS_READ: 'franchise.leads.read',
  FRANCHISE_LEADS_WRITE: 'franchise.leads.write',
  FRANCHISE_LEADS_DELETE: 'franchise.leads.delete',
  
  // Franchise - Lists
  FRANCHISE_LISTS_READ: 'franchise.lists.read',
  FRANCHISE_LISTS_WRITE: 'franchise.lists.write',
  FRANCHISE_LISTS_DELETE: 'franchise.lists.delete',
  
  // Franchise - Tasks
  FRANCHISE_TASKS_READ: 'franchise.tasks.read',
  FRANCHISE_TASKS_WRITE: 'franchise.tasks.write',
  FRANCHISE_TASKS_DELETE: 'franchise.tasks.delete',
  
  // Documents
  DOCUMENTS_READ: 'documents.read',
  DOCUMENTS_WRITE: 'documents.write',
  DOCUMENTS_DELETE: 'documents.delete',
  
  // Social Media
  SOCIAL_READ: 'social.read',
  SOCIAL_WRITE: 'social.write',
  SOCIAL_DELETE: 'social.delete',
  SOCIAL_ACCOUNTS_MANAGE: 'social.accounts.manage',
  
  // Templates
  TEMPLATES_READ: 'templates.read',
  TEMPLATES_WRITE: 'templates.write',
  TEMPLATES_DELETE: 'templates.delete',
  
  // Profiles
  PROFILES_READ: 'profiles.read',
  PROFILES_WRITE: 'profiles.write',
  
  // Admin - Users
  ADMIN_USERS_READ: 'admin.users.read',
  ADMIN_USERS_WRITE: 'admin.users.write',
  ADMIN_USERS_DELETE: 'admin.users.delete',
  
  // Admin - Roles
  ADMIN_ROLES_READ: 'admin.roles.read',
  ADMIN_ROLES_WRITE: 'admin.roles.write',
  ADMIN_ROLES_DELETE: 'admin.roles.delete',
  
  // Admin - Teams
  ADMIN_TEAMS_READ: 'admin.teams.read',
  ADMIN_TEAMS_WRITE: 'admin.teams.write',
  ADMIN_TEAMS_DELETE: 'admin.teams.delete',
  
  // Admin - Settings
  ADMIN_SETTINGS_READ: 'admin.settings.read',
  ADMIN_SETTINGS_WRITE: 'admin.settings.write',
  
  // Admin - Backup
  ADMIN_BACKUP_READ: 'admin.backup.read',
  ADMIN_BACKUP_WRITE: 'admin.backup.write',
  
  // Telnyx
  TELNYX_READ: 'telnyx.read',
  TELNYX_WRITE: 'telnyx.write',
  TELNYX_CALL: 'telnyx.call',
};

/**
 * Menu keys that map to accessible_menus from the backend
 */
export const MENU_KEYS = {
  DASHBOARD: 'dashboard',
  DAILY_LOG: 'daily_log',
  HISTORY: 'history',
  LEADERBOARD: 'leaderboard',
  CRM: 'crm',
  CRM_COMPANIES: 'crm_companies',
  CRM_LEADS: 'crm_leads',
  CRM_INBOX: 'crm_inbox',
  CRM_TASKS: 'crm_tasks',
  FRANCHISE: 'franchise',
  FRANCHISE_LEADS: 'franchise_leads',
  FRANCHISE_LISTS: 'franchise_lists',
  FRANCHISE_TASKS: 'franchise_tasks',
  DOCUMENTS: 'documents',
  SOCIAL: 'social',
  TEMPLATES: 'templates',
  PROFILES: 'profiles',
  ADMIN: 'admin',
  ADMIN_USERS: 'admin_users',
  ADMIN_TEAMS: 'admin_teams',
  ADMIN_SETTINGS: 'admin_settings',
  ADMIN_BACKUP: 'admin_backup',
};

export default usePermissions;
